import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { SURVEY_SUBMISSION_LIST_PAGE_SIZE } from '../constants/survey.constants';
import type { SurveySubmissionFilters, SurveySubmissionListParams } from '../types/survey.types';
import {
  toSurveySubmissionFilterSearch,
  toSurveySubmissionFilterValues,
} from '../utils/surveySubmissionFilters';

const route = getRouteApi('/_auth/feedback/surveys/$surveyId/submissions');

const normalizeFilterValues = (values: Record<string, string>) =>
  toSurveySubmissionFilterValues(toSurveySubmissionFilterSearch(values));

export function useSurveySubmissionFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedValues = useMemo(() => toSurveySubmissionFilterValues(search), [search]);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toSurveySubmissionFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const summaryQueryParams = useMemo<SurveySubmissionFilters>(
    () => toSurveySubmissionFilterSearch(appliedValues),
    [appliedValues],
  );

  const listQueryParams = useMemo<Omit<SurveySubmissionListParams, 'page'>>(
    () => ({ ...summaryQueryParams, limit: SURVEY_SUBMISSION_LIST_PAGE_SIZE }),
    [summaryQueryParams],
  );

  return { ...filters, summaryQueryParams, listQueryParams };
}
